export const AUDIENCE_TOPICS = ['belgium-in-maps', 'horizon-mapper'] as const
export type AudienceTopic = (typeof AUDIENCE_TOPICS)[number]

interface CapturedAudiencePreferences {
  topics: AudienceTopic[]
}

let preferenceToken: string | null = null
let capturedPreferences: CapturedAudiencePreferences | null = null
let sensitiveAudienceStateCaptured = false
let subscriptionConfirmationCaptured = false

function normalizeTopics(value: string | null): AudienceTopic[] {
  const requested = new Set((value ?? '').split(','))
  return AUDIENCE_TOPICS.filter((topic) => requested.has(topic))
}

/**
 * Capture the bearer synchronously and scrub the entire fragment before React,
 * Clarity, Cloudflare Web Analytics, or any other deferred script can run.
 * The bearer is intentionally held only in this module closure.
 */
export function captureAudiencePreferencesFromLocation(): void {
  if (typeof window === 'undefined') return
  const fragment = new URLSearchParams(window.location.hash.slice(1))
  const hasPreferenceFragment = fragment.has('preferences')
  const query = new URLSearchParams(window.location.search)
  const hasSubscriptionState = query.has('subscription')
  subscriptionConfirmationCaptured = hasSubscriptionState
  if (!hasPreferenceFragment && !hasSubscriptionState) return
  sensitiveAudienceStateCaptured = hasPreferenceFragment
  const candidate = fragment.get('preferences') ?? ''
  const topics = normalizeTopics(fragment.get('topics'))
  query.delete('subscription')
  const remainingQuery = query.toString()
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${remainingQuery ? `?${remainingQuery}` : ''}`,
  )
  if (!hasPreferenceFragment || candidate.length < 32) return
  preferenceToken = candidate
  capturedPreferences = {
    topics: topics.length ? topics : ['belgium-in-maps'],
  }
}

captureAudiencePreferencesFromLocation()

export function hasAudiencePreferenceToken(): boolean {
  return preferenceToken !== null
}

export function audiencePreferencesSuppressClarity(): boolean {
  return sensitiveAudienceStateCaptured
}

/** Exposes only that a confirmation return occurred, never its query value. */
export function audienceConfirmationWasCaptured(): boolean {
  return subscriptionConfirmationCaptured
}

export function initialAudienceTopics(): AudienceTopic[] {
  return [...(capturedPreferences?.topics ?? [])]
}

interface PreferenceUpdateResult {
  accepted: boolean
  canContinue: boolean
}

export async function updateAudiencePreferences(
  apiOrigin: string,
  topics: AudienceTopic[],
): Promise<PreferenceUpdateResult> {
  const bearer = preferenceToken
  if (!bearer) return { accepted: false, canContinue: false }
  const response = await fetch(`${apiOrigin}/v1/subscriptions/preferences`, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token: bearer, topics }),
  })
  if (!response.ok) return { accepted: false, canContinue: true }
  let rotatedToken: string | null = null
  try {
    const payload = await response.json() as { preferenceToken?: unknown }
    if (typeof payload.preferenceToken === 'string' && payload.preferenceToken.length >= 32) {
      rotatedToken = payload.preferenceToken
    }
  } catch {
    // Generic accepted responses deliberately contain no replacement bearer.
  }
  preferenceToken = rotatedToken
  if (response.ok) capturedPreferences = { topics: [...topics] }
  return { accepted: true, canContinue: rotatedToken !== null }
}
